import { DOCUMENT } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  ViewChild,
  computed,
  effect,
  inject,
  viewChild,
} from '@angular/core';
import { Router, RouterOutlet } from '@angular/router';

import {
  MenuButtonItem,
  NavbarComponent,
  NavbarConfig,
  NbxDialogOutletComponent,
  NbxMenuItem,
  SidebarComponent,
  SidebarConfig,
} from '@nbx/components';
import { TranslationStore } from '@nbx/core/language';

import { PrimeNG } from 'primeng/config';
import { ToastModule } from 'primeng/toast';

import { LANGUAGE_MENU_ENABLED } from '@core/config/language.config';
import { NAVIGATION_BOTTOM_ITEMS, NAVIGATION_ITEMS } from '@core/config/navigation.config';
import { AuthStore } from '@store/auth.store';
import { UiStore } from '@store/ui.store';

@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [ToastModule, RouterOutlet, SidebarComponent, NavbarComponent, NbxDialogOutletComponent],
})
export class AppComponent {
  private readonly _document = inject(DOCUMENT);
  private readonly _router = inject(Router);
  private readonly _authStore = inject(AuthStore);
  private readonly _translationState = inject(TranslationStore);
  private readonly _uiStore = inject(UiStore);
  private readonly _primeng = inject(PrimeNG);

  private readonly _navEnd = this._uiStore.navigationEnd;
  @ViewChild('sidebar') sidebar!: SidebarComponent;

  isExternalRoute = this._uiStore.isExternalRoute;
  mainContent = viewChild<ElementRef<HTMLElement>>('mainContent');

  mainScrollEnabled = this._uiStore.mainScrollEnabled;
  mainScrolled = this._uiStore.mainScrolled;
  breadcrumbs = computed(() =>
    this._uiStore.breadcrumbs().filter(crumb => !crumb.label.startsWith(':'))
  );

  languageMenuItems = computed<MenuButtonItem[]>(() =>
    this._translationState.getLanguageMenuItems()
  );
  selectedLanguage = this._translationState.activeLanguage;

  userMenuItems = computed<NbxMenuItem[]>(() => [
    {
      label: 'USER.LOGOUT',
      translationKey: 'USER.LOGOUT',
      value: 'logout',
      icon: 'pi pi-sign-out',
    },
  ]);

  sidebarConfig = computed<SidebarConfig>(() => ({
    logo: 'img/logos/rentsody.svg',
    collapsedLogo: '/icons/favicon.svg',
    logoAlt: 'Rentsody',
    items: NAVIGATION_ITEMS,
    bottomItems: NAVIGATION_BOTTOM_ITEMS,
  }));

  navbarStyleClass = computed(() => {
    const base = 'py-4 px-12 [&_.navbar-actions]:gap-6 relative z-10 transition-shadow';
    return this.mainScrolled() ? `${base} shadow-sm` : base;
  });

  navbarConfig = computed<NavbarConfig>(() => ({
    showMenuButton: true,
    breadcrumbs: this.breadcrumbs(),
    actions: [
      ...(LANGUAGE_MENU_ENABLED
        ? [
            {
              icon: 'pi pi-globe',
              tooltip: 'A11Y.LANGUAGE',
              type: 'menu' as const,
              menuItems: this.languageMenuItems(),
              selectedMenuItemValue: this.selectedLanguage(),
              onSelect: (value: string) => this.handleSelectLanguage(value),
            },
          ]
        : []),
      {
        icon: 'pi pi-user',
        tooltip: 'USER.MENU_ARIA',
        type: 'menu',
        menuItems: this.userMenuItems(),
        onSelect: (value: string) => {
          if (value === 'logout') {
            this.handleLogout();
          }
        },
      },
    ],
  }));

  constructor() {
    const availableLanguages = ['es', 'en', 'de', 'pt'];
    this._translationState.initialize(availableLanguages);

    effect(() => {
      const nav = this._navEnd();
      if (!nav) {
        return;
      }
      this.mainContent()?.nativeElement?.scrollTo({ top: 0 });
    });

    // Anchor PrimeNG overlays to the scrollable content area instead of each field
    // (the 'self' default), so dropdowns escape any clipping ancestor (accordions,
    // cards) while still scrolling with the page rather than detaching like body-append.
    effect(() => {
      const main = this.mainContent()?.nativeElement;
      this._primeng.overlayAppendTo.set(main ?? 'self');
    });

    // PrimeNG's copy, aria and date names follow the language through providePrimeNgLocale
    // (app.config.ts); here only the document language.
    effect(() => {
      this._document.documentElement.lang = this._translationState.activeLanguage();
    });
  }

  onMainScroll(event: Event): void {
    const scrolled = (event.target as HTMLElement).scrollTop > 0;
    if (scrolled !== this.mainScrolled()) {
      this._uiStore.setMainScrolled(scrolled);
    }
  }

  toggleSidebar(): void {
    this.sidebar.collapsed.set(!this.sidebar.collapsed());
  }

  handleSelectLanguage(language: string): void {
    this._translationState.setActiveLanguage(language);
  }

  handleLogout(): void {
    this._authStore.logout();
  }
}
